// Die eine Stelle, an der entschieden wird, ob KIsha sichtbar ist.
//
// Vorher stand diese Entscheidung an sieben Orten im Code (Section,
// FrontSearchInputs, SidebarActions, Classic, Cross) und war schon dort nicht
// mehr einheitlich: drei Stellen versteckten KIsha zusaetzlich im iFrame und in
// der nativen App, vier nicht. Wer eine Regel ergaenzt, ergaenzt sie sonst
// siebenmal - und vergisst die achte.
//
// Zwei Wege fuehren zur Sichtbarkeit:
//   1. Die Agentur ist freigeschaltet (properties.ai_travelassistant).
//   2. Testphase: die gewaehlte Destination steht auf der Liste
//      properties.ai_travelassistant_destinations.
// Sie sind ein ODER - Weg 2 schaltet frei, wo Weg 1 es nicht tut, und nimmt
// nie etwas weg. Fehlt die Liste, gilt allein das Agentur-Flag wie bisher.
//
// Darueber steht der Testschalter ?kisha=1 / ?kisha=0 aus der URL. Er sticht
// beide Wege, in beide Richtungen, und haelt fuer die Dauer der Sitzung -
// sonst waere er nach dem ersten Klick wieder weg.

// Wo das Symbol sitzt. Das ist keine Kosmetik, sondern Teil der Regel:
// das freischwebende Symbol war im iFrame und in der App noch nie erwuenscht,
// die eingebetteten Einstiege dagegen schon.
export const KISHA_SURFACES = {
  INLINE: "inline",
  FLOATING: "floating",
};

const norm = (value) => String(value ?? "").trim().toLowerCase();

// Alle Bezeichner, unter denen eine gewaehlte Destination auftreten kann.
// Die Suchmaske liefert je nach Herkunft label/Name/value als Text und
// CityID/RegionID/CountryID/HotelID als Zahl - eine Testliste soll beides
// treffen duerfen ("Tuerkei" ebenso wie "4711").
const destinationTokens = (selectedDestination) => {
  if (!Array.isArray(selectedDestination)) return [];

  const tokens = [];
  for (const entry of selectedDestination) {
    if (!entry) continue;
    for (const key of ["label", "Name", "value", "City", "Country", "CityID", "RegionID", "CountryID", "HotelID"]) {
      const token = norm(entry[key]);
      if (token) tokens.push(token);
    }
  }
  return tokens;
};

// Trifft die Testliste die aktuelle Destination? Ein Listeneintrag trifft,
// wenn er in einem der Bezeichner vorkommt - "turkey" faengt damit auch
// "Turkey, Antalya", ohne dass jemand die genaue Schreibweise kennen muss.
export const matchesDestinationRule = (selectedDestination, allowedDestinations) => {
  if (!Array.isArray(allowedDestinations) || allowedDestinations.length === 0) return false;

  const tokens = destinationTokens(selectedDestination);
  if (tokens.length === 0) return false;

  return allowedDestinations.some((allowed) => {
    const needle = norm(allowed);
    if (!needle) return false;
    return tokens.some((token) => token === needle || token.includes(needle));
  });
};

// Testschalter aus der URL: ?kisha=1 zeigt KIsha, ?kisha=0 versteckt sie.
// Die Entscheidung wandert in sessionStorage, damit sie das Navigieren
// ueberlebt; ein neuer Tab faengt wieder ohne an. Liefert true/false, oder
// null, wenn niemand geschaltet hat.
//
// Alle Zugriffe defensiv: in der nativen App und im Test gibt es kein window,
// und ein Browser mit gesperrtem Storage wirft beim blossen Lesen.
const STORAGE_KEY = "kisha_override";

const readOverrideFromUrl = () => {
  let fromUrl = null;
  try {
    fromUrl = new URLSearchParams(window.location.search).get("kisha");
  } catch {
    return null;
  }

  if (fromUrl === null || fromUrl === "") return null;

  const on = fromUrl === "1" || fromUrl.toLowerCase() === "true";
  try {
    window.sessionStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    // Storage gesperrt - der Schalter gilt dann nur fuer diese eine Seite.
  }
  return on;
};

// Einmal beim Laden des Moduls, und das ist keine Bequemlichkeit: nach der
// ersten Suche schreibt api.js die URL auf "/?id=...&v=..." um und wirft dabei
// jeden anderen Parameter weg. Wer erst beim Rendern von Section nachsieht,
// findet ?kisha=1 nicht mehr vor.
const overrideAtLoad = readOverrideFromUrl();

export const readKIshaOverride = () => {
  if (overrideAtLoad !== null) return overrideAtLoad;

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch {
    return null;
  }

  return null;
};

// Haengt den Testschalter an eine URL, solange er aktiv ist. Noetig ueberall dort,
// wo der Client eine neue Seite oeffnet: sessionStorage gilt pro Tab, ein
// window.open startet also ohne den Schalter, und beim Umschreiben der URL nach
// der Suche wuerde er sonst ebenfalls verlorengehen.
export const withKIshaParam = (url) => {
  const override = readKIshaOverride();
  if (override === null) return url;
  return url + (url.includes("?") ? "&" : "?") + "kisha=" + (override ? "1" : "0");
};

// Die Entscheidung selbst. Bewusst ohne Redux, damit sie ohne laufende App
// pruefbar bleibt; den Zustand holt der Hook useKIshaVisible.
export const isKIshaVisible = ({
  agencyproperties,
  selectedDestination,
  isIFrame = false,
  isNativeApp = false,
  surface = KISHA_SURFACES.INLINE,
  override = null,
} = {}) => {
  // Der Testschalter zuerst und ohne Ausnahme: wer ?kisha=1 setzt, will KIsha
  // sehen - auch eingebettet, auch bei abgeschalteter Agentur.
  if (override === true) return true;
  if (override === false) return false;

  // Das freischwebende Symbol bleibt in Einbettung und App aussen vor -
  // unveraendertes Verhalten der bisherigen drei Stellen.
  if (surface === KISHA_SURFACES.FLOATING && (isIFrame || isNativeApp)) return false;

  if (agencyproperties?.ai_travelassistant) return true;

  return matchesDestinationRule(selectedDestination, agencyproperties?.ai_travelassistant_destinations);
};
